/**
 * useOverlap — Overlap Engine trigger, review queue, and accept/reject actions.
 * Blueprint: F-05 (API contract) + F-06 (auto-confirm logic)
 *
 * Auto-confirm rule: confidence='confirmed' → status=CONFIRMED immediately.
 * Human review:      all CONFIRMED flags surface in OverlapReviewDashboard.
 * Probable:          status=PENDING_REVIEW, DEF_OVERLAP_PENDING fires after 5 biz days.
 */
import { useState, useCallback } from 'react';
import type { OverlapFlag, OverlapEngineResponse } from '@/types/cmm/claimant.types';

interface UseOverlapReturn {
  overlaps:       OverlapFlag[];
  reviewQueue:    OverlapFlag[];  // All CONFIRMED pending human review
  isLoading:      boolean;
  error:          string | null;
  triggerOverlapCheck: (claimantId: string) => Promise<OverlapEngineResponse | null>;
  fetchReviewQueue:    () => Promise<void>;
  acceptOverlap:       (overlapId: string) => Promise<void>;
  rejectOverlap:       (overlapId: string) => Promise<void>;
  bulkAccept:          (overlapIds: string[]) => Promise<void>;
}

export function useOverlap(claimantId?: string): UseOverlapReturn {
  const [overlaps,    setOverlaps]    = useState<OverlapFlag[]>([]);
  const [reviewQueue, setReviewQueue] = useState<OverlapFlag[]>([]);
  const [isLoading,   setIsLoading]   = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  const triggerOverlapCheck = useCallback(async (id: string) => {
    setIsLoading(true);
    try {
      // TODO: POST /claimants/:id/overlap-check
      //   Maps CMM claimant fields → Archetype Overlap Engine payload (F-05)
      //   Response: confidence='confirmed' → auto-set status=CONFIRMED (F-06)
      //   DPV mapping: Y→confirmed | D/S→probable | N→no match
      console.warn('[useOverlap] triggerOverlapCheck — TODO: POST /claimants/:id/overlap-check', id);
      return null;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchReviewQueue = useCallback(async () => {
    setIsLoading(true);
    try {
      // TODO: GET /overlap/review-queue — returns all CONFIRMED overlaps pending Reviewer action
      // setReviewQueue(res.data);
      setReviewQueue([]);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const acceptOverlap = useCallback(async (overlapId: string) => {
    // TODO: PUT /overlap/:id/accept — sets reviewed_by_user_id, status stays CONFIRMED
    console.warn('[useOverlap] acceptOverlap — TODO:', overlapId);
  }, []);

  const rejectOverlap = useCallback(async (overlapId: string) => {
    // TODO: PUT /overlap/:id/reject — status→REJECTED, removes tag from overlap_tags JSONB
    console.warn('[useOverlap] rejectOverlap — TODO:', overlapId);
  }, []);

  const bulkAccept = useCallback(async (overlapIds: string[]) => {
    // TODO: POST /overlap/bulk-accept — F-06 bulk accept from OverlapReviewDashboard
    console.warn('[useOverlap] bulkAccept — TODO:', overlapIds);
  }, []);

  return {
    overlaps, reviewQueue, isLoading, error,
    triggerOverlapCheck, fetchReviewQueue,
    acceptOverlap, rejectOverlap, bulkAccept,
  };
}
